<template>
  <div class="generative-viz">
    <!-- AI Analyzing Overlay -->
    <div v-if="analyzing" class="analyzing-layer">
      <div class="scanner"></div>
      <div class="matrix-text">
        <div v-for="(line, i) in consoleLines" :key="i" class="line">{{ line }}</div>
      </div>
      <div class="status">
        <i class="el-icon-cpu"></i>
        <span>AI 正在解析知识点: "{{ knowledgePoint }}"</span>
        <div class="progress-bar">
          <div class="progress-inner" :style="{ width: progress + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- Generated Content -->
    <div v-else class="generated-content animate-fade">
      <div class="viz-header" >
        <div class="brief" v-latex>
          <i class="el-icon-magic-stick"></i> 
          <strong>AI 动态生成：</strong>这是为您实时构建的 <strong>{{ knowledgePoint }}</strong> 物理/数学演示模型。
        </div>
        <div class="formula" v-latex>$<strong>Mode:</strong> {{ generationMode }} | <strong>Accuracy:</strong> 98.4%$</div>
      </div>

      <div class="canvas-box simulation glass">
        <!-- Generative SVG Engine -->
        <svg viewBox="0 0 800 500" preserveAspectRatio="xMidYMid meet">
          <!-- Common Grid -->
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(0,210,255,0.05)" stroke-width="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          <!-- Dynamic Mode Rendering -->
          <g v-if="generationMode === 'particle'">
            <circle 
              v-for="(p, i) in particles" :key="i"
              :cx="p.x" :cy="p.y" :r="p.r"
              :fill="p.color"
              :filter="p.glow ? 'url(#glow)' : ''"
              opacity="0.8"
            />
            <line 
              v-for="(link, i) in particleLinks" :key="'l'+i"
              :x1="particles[link.a].x" :y1="particles[link.a].y"
              :x2="particles[link.b].x" :y2="particles[link.b].y"
              stroke="rgba(0,210,255,0.2)" stroke-width="1" stroke-dasharray="4"
            />
          </g>

          <g v-if="generationMode === 'wave'">
            <path 
              v-for="i in 3" :key="i"
              :d="generateWavePath(i)"
              fill="none"
              :stroke="i === 1 ? '#00d2ff' : 'rgba(0,210,255,0.3)'"
              :stroke-width="4 - i"
            />
          </g>

          <g v-if="generationMode === 'geometric'">
            <polygon 
              points="400,100 200,400 600,400" 
              fill="rgba(0,210,255,0.1)" 
              stroke="#00d2ff" 
              stroke-width="2"
              class="pulse-shape"
            />
            <circle cx="400" cy="300" r="100" fill="none" stroke="#ffd700" stroke-width="1" stroke-dasharray="5" />
          </g>

          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </svg>

        <!-- HUD Info -->
        <div class="hud-top">
          <div class="stat">
            <label>计算核心</label>
            <div class="value">FrontEnd-Gen-V1</div>
          </div>
          <div class="stat">
            <label>场景熵值</label>
            <div class="value">{{ sceneEntropy.toFixed(2) }}</div>
          </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="row">
          <el-button type="primary" size="small" @click="recalculate" icon="el-icon-refresh">重新生成逻辑</el-button>
          <el-tag type="info" size="small">知识点未命中，已启动前端启发式建模</el-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GenerativeAIViz',
  props: {
    knowledgePoint: { type: String, default: '未知探索' }
  },
  data() {
    return {
      analyzing: true,
      progress: 0,
      consoleLines: [],
      particles: [],
      particleLinks: [],
      generationMode: 'particle',
      sceneEntropy: 1.0,
      time: 0,
      timer: null
    }
  },
  mounted() {
    this.startAnalysis();
  },
  beforeDestroy() {
    cancelAnimationFrame(this.timer);
  },
  methods: {
    startAnalysis() {
      this.analyzing = true;
      this.progress = 0;
      this.consoleLines = [];
      const lines = [
        "> Initializing Generative Engine...",
        "> Pulling Semantic Tokens: " + this.knowledgePoint,
        "> Analyzing Mathematical Symmetries...",
        "> Constructing Heuristic Grid...",
        "> Deploying Physics Particle System...",
        "> Stabilizing Visualization Orbit..."
      ];
      
      let i = 0;
      const interval = setInterval(() => {
        if (i < lines.length) {
          this.consoleLines.push(lines[i]);
          this.progress += 100 / lines.length;
          i++;
        } else {
          clearInterval(interval);
          setTimeout(() => {
            this.finalizeGeneration();
          }, 800);
        }
      }, 400);
    },
    finalizeGeneration() {
      this.analyzing = false;
      this.selectMode();
      this.initParticles();
      this.animate();
    },
    selectMode() {
      const kp = this.knowledgePoint;
      if (kp.includes('波') || kp.includes('光') || kp.includes('场')) {
        this.generationMode = 'wave';
      } else if (kp.includes('三角形') || kp.includes('形') || kp.includes('几何')) {
        this.generationMode = 'geometric';
      } else {
        this.generationMode = 'particle';
      }
    },
    initParticles() {
      this.particles = Array.from({ length: 20 }, () => ({
        x: Math.random() * 800,
        y: Math.random() * 500,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        r: 3 + Math.random() * 5,
        color: Math.random() > 0.5 ? '#00d2ff' : '#ffd700',
        glow: Math.random() > 0.7
      }));
      
      // Random links
      this.particleLinks = [];
      for (let i = 0; i < 15; i++) {
        this.particleLinks.push({
          a: Math.floor(Math.random() * 20),
          b: Math.floor(Math.random() * 20)
        });
      }
    },
    animate() {
      this.time += 0.02;
      this.sceneEntropy = 1.0 + Math.sin(this.time) * 0.2;
      
      if (this.generationMode === 'particle') {
        this.particles.forEach(p => {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > 800) p.vx *= -1;
          if (p.y < 0 || p.y > 500) p.vy *= -1;
        });
      }
      
      this.timer = requestAnimationFrame(this.animate);
    },
    generateWavePath(offset) {
      let d = `M 0,${250 + offset * 20}`;
      for (let x = 0; x <= 800; x += 10) {
        const y = 250 + offset * 20 + Math.sin(x * 0.01 + this.time * 2 + offset) * 50;
        d += ` L ${x},${y}`;
      }
      return d;
    },
    recalculate() {
      this.startAnalysis();
    }
  }
}
</script>

<style scoped lang="scss">
.generative-viz {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #02040a;
}

.analyzing-layer {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at center, #0a1120 0%, #02040a 100%);
  color: #00d2ff;
  font-family: 'Consolas', monospace;
  position: relative;
  overflow: hidden;

  .scanner {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #00d2ff, transparent);
    box-shadow: 0 0 15px #00d2ff;
    animation: scan 3s infinite linear;
    z-index: 5;
  }

  .matrix-text {
    width: 80%;
    height: 150px;
    background: rgba(0, 0, 0, 0.4);
    padding: 20px;
    border: 1px solid rgba(0, 210, 255, 0.2);
    margin-bottom: 40px;
    overflow: hidden;
    .line {
      font-size: 0.9rem;
      margin-bottom: 5px;
      opacity: 0.8;
      &:last-child { opacity: 1; color: #fff; text-shadow: 0 0 5px #00d2ff; }
    }
  }

  .status {
    text-align: center;
    span { font-size: 1.1rem; letter-spacing: 2px; }
    i { margin-right: 10px; font-size: 1.5rem; animation: rotate 2s linear infinite; }
  }

  .progress-bar {
    width: 300px;
    height: 4px;
    background: rgba(255, 255, 255, 0.05);
    margin-top: 20px;
    border-radius: 2px;
    overflow: hidden;
    .progress-inner {
      height: 100%;
      background: #00d2ff;
      box-shadow: 0 0 10px #00d2ff;
      transition: width 0.3s;
    }
  }
}

.pulse-shape {
  animation: pulse 4s ease-in-out infinite;
}

@keyframes scan {
  from { top: -10%; }
  to { top: 110%; }
}

@keyframes rotate {
  from { transform: rotate(0); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.05); }
}

.animate-fade {
  animation: fadeIn 0.8s ease-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
